/* eslint-disable react/jsx-pascal-case */
import { useContext, useEffect, useState } from "react";
import GeneralContext from "../../../context/GeneralContext/GeneralContext";
import api from "../../../services/api";
import { displayMessage } from "../../../helpers/messages";
import SL_Button from "../../../components/buttons/Button";
import { SL_Input } from "../../../components/input/Input";
import { Form, Modal, Skeleton, Tabs } from "antd";
import { GridFooter } from "@mui/x-data-grid-pro";
import CustomToolbar from "../../../components/grid/CustomToolbar";
import { SL_Grid } from "../../../components/grid/SL_Grid";
import stretching_exercise_grid_columns from "./grid_columns/stretching_exercise_grid_columns";
import {
	DeleteOutlined,
	PlusCircleOutlined,
	EditOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { CreateStretchingExerciseModal } from "./CreateStretchingExerciseModal";
import { DetailStretchingExerciseModal } from "./DetailStretchingExerciseModal";

export function DetailStretchingModal({ onCloseModal, stretchingId }) {
	const [modal, contextHolder] = Modal.useModal();
	const [isLoading, setIsLoading] = useState(true);
	const [stretching, setStretching] = useState(null);
	const [exercises, setExercises] = useState([]);
	const [currentExercise, setCurrentExercise] = useState(null);
	const [isCreateExerciseOpen, setIsCreateExerciseOpen] = useState(false);
	const [isDetailExerciseOpen, setIsDetailExerciseOpen] = useState(false);
	const [form] = Form.useForm();

	const { setShowLoading, mobile_mode } = useContext(GeneralContext);

	useEffect(() => {
		loadStretching();
	}, [stretchingId]);

	async function loadStretching() {
		setIsLoading(true);
		try {
			const response = await api.stretchings.find(stretchingId);
			const stretchingData = response.data;
			setStretching(stretchingData);
			setExercises(stretchingData.exercises || []);

			form.setFieldsValue({
				name: stretchingData.name,
				description: stretchingData.description,
				duration_minutes: stretchingData.duration_minutes,
				order_index: stretchingData.order_index,
			});
		} catch (error) {
			console.error("Erro ao carregar alongamento:", error);
		} finally {
			setIsLoading(false);
		}
	}

	async function onFinishForm(values) {
		try {
			setShowLoading(true);
			await api.stretchings.update(stretchingId, values);
			await onCloseModal(true);
		} catch (error) {
			console.error("Erro ao atualizar alongamento:", error);
		} finally {
			setShowLoading(false);
		}
	}

	function handleExerciseRowChange(e) {
		setCurrentExercise(e?.row);
	}

	function onClickRemoveExercise() {
		modal.confirm({
			centered: true,
			content: (
				<div className="text-base w-full text-fit-red-500 text-center sm:text-start">
					{displayMessage("CONFIRM_REMOVE_EXERCISE")}
				</div>
			),
			cancelText: displayMessage("CANCEL"),
			okText: displayMessage("CONFIRM"),
			okButtonProps: { danger: true },
			onOk: removeExercise,
			icon: mobile_mode ? null : <WarningOutlined style={{ color: "red" }} />,
		});
	}

	async function removeExercise() {
		try {
			setShowLoading(true);
			await api.stretchingExercises.delete(currentExercise.id);
			setCurrentExercise(null);
			await loadStretching();
		} finally {
			setShowLoading(false);
		}
	}

	async function onCloseCreateExercise(reload = false) {
		if (reload) await loadStretching();
		setIsCreateExerciseOpen(false);
	}

	async function onCloseDetailExercise(reload = false) {
		if (reload) await loadStretching();
		setIsDetailExerciseOpen(false);
	}

	const tabItems = [
		{
			key: "1",
			label: displayMessage("EDIT_WORKOUT"),
			children: (
				<Form
					onFinish={onFinishForm}
					layout="vertical"
					form={form}
					className={`flex gap-2 flex-col`}
				>
					<Form.Item
						label={
							<span className="text-red-700 font-semibold">
								{displayMessage("WORKOUT_NAME")}:
							</span>
						}
						name="name"
						className={`w-full m-0`}
						required
						rules={[
							{ required: true, message: displayMessage("NAME_MANDATORY") },
						]}
					>
						<SL_Input type="text" className="text-red-700" />
					</Form.Item>

					<Form.Item
						label={
							<span className="text-red-700 font-semibold">
								{displayMessage("DESCRIPTION")}:
							</span>
						}
						name="description"
						className={`w-full m-0`}
					>
						<SL_Input textarea={true} rows={2} className="text-red-700" />
					</Form.Item>

					<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
						<Form.Item
							label={
								<span className="text-red-700 font-semibold">
									{displayMessage("DURATION_MINUTES")}:
								</span>
							}
							name="duration_minutes"
							className={`w-full m-0`}
						>
							<SL_Input type="number" className="text-red-700" />
						</Form.Item>

						<Form.Item
							label={
								<span className="text-red-700 font-semibold">
									{displayMessage("ORDER_INDEX")}:
								</span>
							}
							name="order_index"
							className={`w-full m-0`}
						>
							<SL_Input type="number" className="text-red-700" />
						</Form.Item>
					</div>
				</Form>
			),
		},
		{
			key: "2",
			label: `${displayMessage("EXERCISES")} (${exercises.length})`,
			children: (
				<div className="flex flex-col gap-2">
					<div className="flex gap-2 flex-wrap">
						<SL_Button
							type="default"
							className="flex items-center"
							onClick={() => setIsCreateExerciseOpen(true)}
							icon={<PlusCircleOutlined />}
						>
							{displayMessage("ADD_EXERCISE")}
						</SL_Button>
						<SL_Button
							type="default"
							disabled={!currentExercise}
							className="flex items-center"
							onClick={() => setIsDetailExerciseOpen(true)}
							icon={<EditOutlined />}
						>
							{displayMessage("EDIT_EXERCISE")}
						</SL_Button>
						<SL_Button
							disabled={!currentExercise}
							danger={true}
							className="flex items-center"
							onClick={onClickRemoveExercise}
							icon={<DeleteOutlined />}
						>
							{displayMessage("REMOVE_EXERCISE")}
						</SL_Button>
					</div>
					<div className="h-[300px]">
						<SL_Grid
							rows={exercises}
							columns={stretching_exercise_grid_columns}
							components={{
								Toolbar: () => <CustomToolbar />,
								Footer: () => <GridFooter />,
							}}
							onRowClick={handleExerciseRowChange}
						/>
					</div>
				</div>
			),
		},
	];

	return (
		<>
			{contextHolder}
			{isCreateExerciseOpen && (
				<CreateStretchingExerciseModal
					onCloseModal={onCloseCreateExercise}
					stretchingId={stretchingId}
				/>
			)}
			{isDetailExerciseOpen && currentExercise && (
				<DetailStretchingExerciseModal
					onCloseModal={onCloseDetailExercise}
					exerciseId={currentExercise.id}
				/>
			)}

			<Modal
				title={
					<div className="text-xl font-bold text-red-700 mr-3">
						{displayMessage("VIEW_WORKOUT")} - {stretching?.name || "..."}
					</div>
				}
				open={true}
				onCancel={() => onCloseModal(false)}
				centered={true}
				footer={
					<div className="flex flex-col ss:flex-row gap-2 justify-end">
						<SL_Button
							className="w-full ss:w-[150px] bg-gray-500 border-transparent"
							onClick={() => onCloseModal(false)}
						>
							{displayMessage("CLOSE")}
						</SL_Button>

						<SL_Button
							className="w-full ss:w-[150px] m-0 bg-red-600"
							onClick={() => form.submit()}
						>
							{displayMessage("SAVE")}
						</SL_Button>
					</div>
				}
				maskClosable={false}
				width={800}
			>
				{isLoading ? <Skeleton active /> : <Tabs items={tabItems} />}
			</Modal>
		</>
	);
}


